import './App.css';
import 'survey-core/survey-core.css';
import { RouterProvider, createHashRouter } from 'react-router-dom';
import React, { useState, Suspense, lazy, useEffect, useMemo } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Accordion from '@mui/material/Accordion';
import Typography from '@mui/material/Typography';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import GreenCheckMark from './img/green_checkmark.svg';
import Logo from './img/rrit_logo.png';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { NagyNav } from './NavBar';
import Button from '@mui/material/Button';
import { PocketBaseProvider, usePocketBase } from './PocketBaseProvider';
import { SnackbarProvider } from './SnackProvider';
import { ThemeModeContext } from './themeModeContext';
import { useRouteError, isRouteErrorResponse } from "react-router-dom";
import { useParams } from "react-router-dom";
import * as Sentry from "@sentry/browser";
import useAsyncError from './AsyncErrorHook';

const AdminLogin = lazy(() => import('./AdminLogin'));
const VFSList = lazy(() => import('./VFSList'));
const RegistrationController = lazy(() => import('./RegistrationController'));
const EventTable = lazy(() => import('./EventTable'));
const DownloadButton = lazy(() => import('./DownloadButton'));
const AdminPanel = lazy(() => import('./AdminPanel'));
const Editor = lazy(() => import('./Editor'));
const MosaicForm = lazy(() => import('./MosaicForm'));
const MosaicViewer = lazy(() => import('./MosaicViewer'));
const UserSearchAndUpdate = lazy(() => import('./UserSearchAndUpdate'));
const SurveyMaker = lazy(() => import('./SurveyMaker'));
const SurveyMakerList = lazy(() => import('./SurveyMakerList'));
const EmailTable = lazy(() => import('./AdminEmailManager'));
const VFSEditor = lazy(() => import('./VFSEditor'));

const THEME_STORAGE_KEY = 'ijr-theme-mode';

function getInitialThemeMode() {
  if (typeof window === 'undefined') {
    return 'light';
  }

  const storedMode = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (storedMode === 'light' || storedMode === 'dark') {
    return storedMode;
  }

  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}


function RouterError() {
  const error = useRouteError();
  console.error(error);
  return (
    <div>
      <Typography variant="h4" component="h1" gutterBottom style={{ marginTop: "20px", textAlign: "center" }}>
        Alkalmazáshiba
      </Typography>
      <div style={{ width: "80vw", maxWidth: "500px", display: "flex", justifyContent: "center", flexDirection: "column", marginLeft: "auto", marginRight: "auto"}}>
      <Typography variant="body1" gutterBottom style={{ marginTop: "10px", textAlign: "justify" }}>
        Hiba lépett fel az alkalmazás működése közben, erről a rendszergazda éretsítést kapott.
      </Typography>
      <Typography variant="body1" gutterBottom style={{ marginTop: "10px", textAlign: "justify" }}>
        Próbálja meg újra betölteni az oldalt és ellenőrizze internetkapcsolatát. Amennyiben a hiba továbbra is fennáll, az alábbi gombra kattintva szövegesen is jelezheti a problémát a rendszergazda felé.
      </Typography>
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: "20px"}}>
      <Button
        variant="outlined"
        color="error"onClick={() => {
          const eventId = Sentry.captureException(error);
        
          Sentry.showReportDialog({
            eventId
          });
        }}
      >
        Visszajelzek
      </Button>
      </div>
    </div>
  );
}

const routes = createHashRouter([
  {
    path: '/',
    errorElement: <RouterError />,
    element: <Rendezvenyek />,
  },
  {
    path: '/admin',
    errorElement: <RouterError />,
    element: <Admin />,
  },
  {
    path: '/admin/:id',
    errorElement: <RouterError />,
    element: <JelentkezesList />,
  },
  {
    path: '/rendezveny/:id',
    errorElement: <RouterError />,
    element: <Jelentkezes />,
  },
  {
    path: '/uj',
    errorElement: <RouterError />,
    element: <Editor />,
  },
  {
    path: '/szerkesztes/:id',
    errorElement: <RouterError />,
    element: <Editor />,
  },
  {
    path: '/koszi',
    errorElement: <RouterError />,
    element: <Koszono />,
  },
  {
    path: '/kosziForm',
    errorElement: <RouterError />,
    element: <KoszonoForm />,
  },
  {
    path: '/kosziSMS',
    errorElement: <RouterError />,
    element: <KoszonoSMS />,
  },
  {
    path: '/mosaicForm/:eventId/:userId',
    errorElement: <RouterError />,
    element: <Mosaic />
  },
  {
    path: '/mosaicViewer/:id',
    errorElement: <RouterError />,
    element: <MosaicView />
  },
  {
    path: '/userSearch',
    errorElement: <RouterError />,
    element: <UserSearchView />
  },
  {
    path: '/surveyMaker',
    errorElement: <RouterError />,
    element: <SurveyMakerView />
  },
  {
    path: '/surveyMaker/szerkesztes/:id',
    errorElement: <RouterError />,
    element: <SurveyMakerView />
  },
  {
    path: '/surveys',
    errorElement: <RouterError />,
    element: <SurveyMakerListView />
  },
  {
    path: '/rsvp/:id',
    errorElement: <RouterError />,
    element: <RsvpPage />
  },
  {
    path: '/vfsEditor',
    errorElement: <RouterError />,
    element: <VFSEditorView />
  },
  {
    path: '/vfsEditor/szerkesztes/:id',
    errorElement: <RouterError />,
    element: <VFSEditorView />
  },
  {
    path: '/vfsList',
    errorElement: <RouterError />,
    element: <VFSListView />
  },
  {
    path: '*',
    element: <Page404 />,
  }
])

function App() {
  const [mode, setMode] = useState(getInitialThemeMode);

  useEffect(() => {
    window.localStorage.setItem(THEME_STORAGE_KEY, mode);
  }, [mode]);

  const theme = useMemo(() => createTheme({
    palette: {
      mode,
      primary: {
        main: '#35393C',
      },
      secondary: {
        main: '#841618',
      },
      error: {
        main: '#B63E40',
      },
      warning: {
        main: '#FF7F11',
      },
      info: {
        main: '#3a5683',
      },
      success: {
        main: '#3eb666',
      },
    },
  }), [mode]);

  function toggleMode() {
    setMode((currentMode) => (currentMode === 'light' ? 'dark' : 'light'));
  }

  return (
      <ThemeModeContext.Provider value={{ mode, toggleMode }}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <PocketBaseProvider>
            <SnackbarProvider>
              <RouterProvider router={routes} />
            </SnackbarProvider>
          </PocketBaseProvider>
        </ThemeProvider>
      </ThemeModeContext.Provider>
  );
}

export default App;



function Rendezvenyek() {

  return (
    <div>
      <NagyNav />
      <p style={{ margin: "10px", textAlign: "center", fontSize: "2em"}}>Rendezvények</p>
      <Suspense fallback={<CircularProgress size="30px" sx={{ display: "block", marginTop: 10, marginLeft: "auto", marginRight: "auto" }} />}>
        <EventTable />
      </Suspense>
    </div>
  );
}

function Admin() {
  const [user, setUser] = useState("logout");

  return (
    <div>
      <NagyNav setUser={setUser} user={user} />
      <OneLevelBreadcrumbs currentName="Rendezvények" />
      {
        (user === "logout") ? <Suspense fallback={<CircularProgress size="30px" sx={{ display: "block", marginTop: 10, marginLeft: "auto", marginRight: "auto" }} />}><AdminLogin setUser={setUser} /></Suspense> : <Suspense fallback={<CircularProgress size="30px" sx={{ display: "block", marginTop: 10, marginLeft: "auto", marginRight: "auto" }} />}><AdminPanel /></Suspense>
      }
    </div>
  );
}

function JelentkezesList() {
  return (
    <div>
      <NagyNav />
      <OneLevelBreadcrumbs currentName="Jelentkezések" />
      <Suspense fallback={<CircularProgress size="30px" />}>
      <div style={{ margin: "10px" }}>
        <EmailTable />
      </div>
      </Suspense>
    </div>
  );
}

function Koszono() {
  return (
    <div>
      <NagyNav />
      <p style={{ marginTop: "20px", marginBottom: "20px", textAlign: "center", fontSize: "2em"}}>Köszönjük a jelentkezését!</p>
      <img src={GreenCheckMark} alt="Success" style={{ width: "30vw", maxWidth: "400px", height: "auto", display: "block", margin: "0 auto"}} />
      <div style={{ display: "grid", placeItems: "center"}}>
      <img src={Logo} alt="Powered By Red Rabbit IT Consulting" style={{ width: "100px", height: "auto", display: "block", marginTop: "20px", marginBottom: "0px"}} />
      <p style={{ textAlign: "center", fontSize: "11px" }}>Powered By RedRabbit ProEvent</p>
      </div>
    </div>
  );
}

function KoszonoSMS() {
  return (
    <div>
      <NagyNav />
      <p style={{ marginTop: "20px", marginBottom: "20px", textAlign: "center", fontSize: "2em"}}>Sikeres SMS kiküldés!</p>
      <p style={{ marginTop: "0px", marginBottom: "20px", textAlign: "center", fontSize: "1em"}}>Az SMS kampány 30 másodperc múlva végrehajtásra kerül a kért listákra.</p>
      <a href="/#admin" style={{ display: "block", textAlign: "center", marginBottom: "20px"}}>Vissza az admin panelhez</a>
      <img src={GreenCheckMark} alt="Success" style={{ width: "30vw", maxWidth: "400px", height: "auto", display: "block", margin: "0 auto"}} />
      <div style={{ display: "grid", placeItems: "center"}}>
      <img src={Logo} alt="Powered By Red Rabbit IT Consulting" style={{ width: "100px", height: "auto", display: "block", marginTop: "20px", marginBottom: "0px"}} />
      <p style={{ textAlign: "center", fontSize: "11px" }}>Powered By RedRabbit ProEvent</p>
      </div>
    </div>
  );
}


function KoszonoForm() {
  return (
    <div>
      <NagyNav />
      <p style={{ marginTop: "20px", marginBottom: "20px", textAlign: "center", fontSize: "2em"}}>Köszönjük a visszajelzését!</p>
      <img src={GreenCheckMark} alt="Success" style={{ width: "30vw", maxWidth: "400px", height: "auto", display: "block", margin: "0 auto"}} />
      <div style={{ display: "grid", placeItems: "center"}}>
      <img src={Logo} alt="Powered By Red Rabbit IT Consulting" style={{ width: "100px", height: "auto", display: "block", marginTop: "20px", marginBottom: "0px"}} />
      <p style={{ textAlign: "center", fontSize: "11px" }}>Powered By RedRabbit ProEvent</p>
      </div>
    </div>
  );
}


function Mosaic() {
  return (
    <div>
      <Suspense fallback={<CircularProgress size="30px" />}>
        <MosaicForm />
      </Suspense>
    </div>
  );
}

function MosaicView() {
  return (
    <div>
      <NagyNav />
      <OneLevelBreadcrumbs currentName="Mosaic" />
      <Suspense fallback={<CircularProgress size="30px" />}>
        <MosaicViewer />
      </Suspense>
    </div>
  );
}

function UserSearchView() {
  return (
    <div>
      <NagyNav />
      <OneLevelBreadcrumbs currentName="Felhasználói adatbank" />
      <Suspense fallback={<CircularProgress size="30px" />}>
        <UserSearchAndUpdate />
      </Suspense>
    </div>
  );
}


function SurveyMakerView() {
  return (
    <div>
      <NagyNav />
      <SurveyTwoLevelBreadcrumbs currentName="Kérdőívszerkesztő" />
      <Suspense fallback={<CircularProgress size="30px" />}>
        <SurveyMaker />
      </Suspense>
    </div>
  );
}

function SurveyMakerListView() {
  return (
    <div>
      <NagyNav />
      <OneLevelBreadcrumbs currentName="Kérdőívek" />
      <Suspense fallback={<CircularProgress size="30px" />}>
        <SurveyMakerList />
      </Suspense>
    </div>
  );
}

function VFSEditorView() {
  return (
    <div>
      <NagyNav />
      <VFSTwoLevelBreadcrumbs currentName="Üzenetszerkesztő" />
      <Suspense fallback={<CircularProgress size="30px" />}>
        <VFSEditor />
      </Suspense>
    </div>
  );
}

function VFSListView() {
  return (
    <div>
      <NagyNav />
      <OneLevelBreadcrumbs currentName="Üzenetek" />
      <Suspense fallback={<CircularProgress size="30px" />}>
        <VFSList />
      </Suspense>
    </div>
  );
}

function RsvpPage() {

  const [rsvpStatus, setRsvpStatus] = useState(null);
  const pb = usePocketBase();
  const throwAsyncError = useAsyncError();

  function sendUserRspv(status) {
    setRsvpStatus("loading");
    const rsvpId = window.location.href.split("/").pop();
    pb.collection("jelentkezesek").update(rsvpId, {
      rsvp: status
    }).then(() => {
      setRsvpStatus("success");
    }).catch((error) => {
      throwAsyncError(error);
    });

  }

  return (
    <div>
      <NagyNav />
      {
        (rsvpStatus === null) ?
        <div style={{ display: "grid", placeItems: "center"}}>
          <h1 style={{ margin: "10px", textAlign: "center", fontSize: "2rem", marginTop: "40px"}}>Visszajelzés részvételről</h1>
          <p style={{ margin: "10px", textAlign: "center"}}>Kérjük, jelezzen vissza, hogy részt tud-e venni a rendezvényen!</p>
          <div style={{ display: "flex", justifyContent: "center", gap: "20px", marginTop: "10px", marginBottom: "10px"}}>
          <Button onClick={ () => sendUserRspv("Jön") } variant="contained" color='success' style={{ display: "block", margin: "10px auto"}}>Igen</Button>
          <Button onClick={ () => sendUserRspv("Nem jön") } variant="contained" color='error' style={{ display: "block", margin: "10px auto"}}>Nem</Button>
          </div>
        </div>
        : (rsvpStatus === "loading") ?
        <div style={{ display: "grid", placeItems: "center"}}>
          <h1 style={{ margin: "10px", textAlign: "center", fontSize: "2rem", marginTop: "40px"}}>Visszajelzés részvételről</h1>
          <CircularProgress size="30px" style={{ margin: "10px"}} />
        </div>
        : (rsvpStatus === "success") ?
        <div style={{ display: "grid", placeItems: "center"}}>
          <p style={{ marginTop: "20px", marginBottom: "20px", textAlign: "center", fontSize: "2em"}}>Köszönjük a visszajelzését!</p>
          <img src={GreenCheckMark} alt="Success" style={{ width: "50vw", maxWidth: "400px", height: "auto", display: "block", margin: "0 auto"}} />
          <div style={{ display: "grid", placeItems: "center"}}>
            <img src={Logo} alt="Powered By Red Rabbit IT Consulting" style={{ width: "100px", height: "auto", display: "block", marginTop: "20px", marginBottom: "0px"}} />
            <p style={{ textAlign: "center", fontSize: "11px" }}>Powered By RedRabbit ProEvent</p>
          </div>
        </div>
        : null
      }
    </div>
  );
}

function Jelentkezes() {

  const [event, setEvent] = useState("loading");
  // get the event ID from the URL
  const { id } = useParams();
  const [ idoPont, setIdoPont ] = useState("loading");
  const [ specialQuestions, setSpecialQuestions ] = useState("loading");
  const pb = usePocketBase();
  const throwAsyncError = useAsyncError();


  React.useEffect(() => {
    pb.autoCancellation = false;
    pb.collection("rendezvenyek").getOne(id,
      {
        expand: "extraQuestions"
      }
    ).then(data => {
      setEvent(data);
      if (data.extraQuestions) {
        setSpecialQuestions(data.expand.extraQuestions);
      } else {
        setSpecialQuestions(null);
      }
      try {
        data.idopont = new Date(data.idopont)
      } catch (error) {
        throwAsyncError(error);
      }
      setIdoPont(new Date(data.idopont).toLocaleDateString() + " " + new Date(data.idopont).getUTCHours() + ":" + new Date(data.idopont).getMinutes().toString().padStart(2, '0'));
    }).catch(error => {
      throwAsyncError(error);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (event === "loading" || specialQuestions === "loading") {
    return (
      <div>
        <CircularProgress size="30px" style={{ margin: "10px"}} />
      </div>
    );
  }

  return (
    <>
    <div>
      <NagyNav />
      <div style={{ display: "grid", placeItems: "center"}}>
      <p style={{ margin: "10px", textAlign: "center", fontSize: "2em"}}>{event.nev}</p>
      <p style={{ marginBottom: "0px", marginLeft: "10px", marginRight: "10px", textAlign: "center"}}><strong>Időpont:</strong> { idoPont }</p>
      <p style={{ marginTop: "0px", marginLeft: "10px", marginRight: "10px", marginBottom: "10px", textAlign: "center"}}><strong>Helyszín:</strong> {event.helyszin}</p>
      <Accordion style={{ width: "90vw", maxWidth: "600px" }}>
        <AccordionSummary
          expandIcon={<ArrowDropDownIcon />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
          <Typography component="span">Részletek</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            <p style={{ textAlign: "justify", margin: "10px"}} dangerouslySetInnerHTML={{ __html: event.leiras }}></p>
          </Typography>
        </AccordionDetails>
      </Accordion>
      <Accordion style={{ width: "90vw", maxWidth: "600px", marginTop: "10px" }}>
        <AccordionSummary
          expandIcon={<ArrowDropDownIcon />}
          aria-controls="panel2-content"
          id="panel2-header"
        >
          <Typography component="span">Program</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <DownloadButton event={event} />
        </AccordionDetails>
      </Accordion>
      </div>
      <div style={{ display: "grid", placeItems: "center", marginTop: "20px", marginBottom: "20px", width: "90vw", maxWidth: "600px", marginLeft: "auto", marginRight: "auto"}}>
      {
        (event.status === "Nyitott" || event.status === "Előjelentkezés") ? <Suspense fallback={<CircularProgress size="30px" />}><RegistrationController helyszin={event.helyszin} idoPont={idoPont} specialQuestions={specialQuestions} /></Suspense> : <p> A rendezvényre jelenleg nem lehet jelentkezni.</p>
      }

      </div>
       </div>
    </>
  );
}

function OneLevelBreadcrumbs({ currentName }) {
  return (
    <Breadcrumbs aria-label="breadcrumb" sx={{ marginBottom: "10px", marginTop: "20px" }}>
      <Link underline="hover" color="inherit" href="#/admin" sx={{ marginLeft: '20px' }}>
        Admin panel
      </Link>
    <Typography sx={{ color: 'text.primary' }}>{currentName}</Typography>
    </Breadcrumbs>
  );
}
function SurveyTwoLevelBreadcrumbs({ currentName }) {
  return (
    <Breadcrumbs aria-label="breadcrumb" sx={{ marginBottom: "10px", marginTop: "20px" }}>
      <Link underline="hover" color="inherit" href="#/admin" sx={{ marginLeft: '20px' }}>
        Admin panel
      </Link>
      <Link underline="hover" color="inherit" href="/#surveys">Kérdőívek</Link>
    <Typography sx={{ color: 'text.primary' }}>{currentName}</Typography>
    </Breadcrumbs>
  );
}

function VFSTwoLevelBreadcrumbs({ currentName }) {
  return (
    <Breadcrumbs aria-label="breadcrumb" sx={{ marginBottom: "10px", marginTop: "20px" }}>
      <Link underline="hover" color="inherit" href="#/admin" sx={{ marginLeft: '20px' }}>
        Admin panel
      </Link>
      <Link underline="hover" color="inherit" href="/#vfsList">Üzenetek</Link>
    <Typography sx={{ color: 'text.primary' }}>{currentName}</Typography>
    </Breadcrumbs>
   );
}

function Page404() {
  return (
    <>
      <NagyNav />
      <div style={{ display: "grid", placeItems: "center", marginTop: "40px"}}>
        <h1 style={{ fontSize: "3em", marginBottom: "20px"}}>404</h1>
        <img src={Logo} alt="Powered By Red Rabbit IT Consulting" style={{ width: "300px", height: "auto", display: "block", marginBottom: "20px"}} />
        <p style={{ fontSize: "1.5em", marginBottom: "20px"}}>A keresett oldal nem található.</p>
        <a href="/#" style={{ fontSize: "1.2em", color: "#3a5683"}}>Vissza a főoldalra</a>
      </div>
    </>
  );
}
